import React, { useMemo, useState } from "react";
import { Copy, Check, Code2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function EmbedDialog({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet = useMemo(
    () =>
      `<iframe\n  src="${origin}/embed"\n  width="100%"\n  height="640"\n  style="border:0;border-radius:14px;overflow:hidden"\n  loading="lazy"\n  sandbox="allow-scripts allow-same-origin allow-popups"\n  referrerpolicy="no-referrer"\n  title="Simon/Myers Isometric World"\n></iframe>`,
    [origin]
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed code copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Could not copy — select the code manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="embed-dialog"
        className="max-w-2xl border-0 p-0 overflow-hidden"
        style={{ background: "#23262B" }}
      >
        <div className="p-6 md:p-7">
          <DialogHeader>
            <span className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-[11px] font-display font-semibold uppercase tracking-[0.18em] text-[#2B2E34]" style={{ background: "#FFCE24" }}>
              <Code2 size={13} /> Drop-in Embed
            </span>
            <DialogTitle className="mt-3 font-display font-bold uppercase tracking-tight text-2xl md:text-3xl text-white leading-[0.95]">
              Put the world on <span style={{ color: "#FFCE24" }}>any</span> page
            </DialogTitle>
            <DialogDescription className="mt-2 font-body text-sm md:text-base text-white/70">
              The experience ships as a secure, sandboxed iframe. Paste one snippet and the
              isometric world flies onto your site — responsive and self-contained.
            </DialogDescription>
          </DialogHeader>

          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 font-display uppercase tracking-wide text-[11px] text-white/60">
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FFCE24" }} /> Sandboxed &amp; isolated</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FFCE24" }} /> Self-contained</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FFCE24" }} /> Works at any size</li>
          </ul>

          <div className="mt-5 rounded-xl border" style={{ background: "#1B1E22", borderColor: "rgba(255,255,255,0.12)" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
              <div className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full" style={{ background: "#FF5F57" }} />
                <span className="h-3 w-3 rounded-full" style={{ background: "#FEBC2E" }} />
                <span className="h-3 w-3 rounded-full" style={{ background: "#28C840" }} />
                <span className="ml-3 font-mono-code text-xs text-white/50">embed.html</span>
              </div>
              <button
                data-testid="embed-copy-button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-display font-semibold uppercase tracking-wide text-[#2B2E34] hover:brightness-95 active:scale-[0.98] transition-[filter]"
                style={{ background: "#FFCE24" }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre
              data-testid="embed-iframe-code"
              className="font-mono-code text-[12px] md:text-[13px] leading-relaxed text-white/85 p-4 overflow-x-auto sm-scroll"
            >
{snippet}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmbedDialog;
